/**
 * `MaintenanceScreen` — GVW-165 standalone state page.
 *
 * Maintenance mode page. Reads ?eta= query param for end time. Status role.
 *
 * Cite: design/30-engineering/ui-implementation/GVW-165/brief.md
 * Copy: error-catalog.md GV-ERR-MAINT, copy-deck.md gvw.165.*
 */

import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { tokens } from '@/design';
import { useLiveRegion } from '@/state/liveRegion';

export function MaintenanceScreen() {
  const liveRegion = useLiveRegion();
  const [searchParams] = useSearchParams();
  
  // Query param per brief.md step 4.f
  const etaParam = searchParams.get('eta');
  const endTime = etaParam 
    ? new Date(etaParam).toLocaleString()
    : 'as soon as possible';

  useEffect(() => {
    // Copy key: gvw.165.title
    document.title = 'Maintenance · GeoViewer';
    // Status role per error-catalog.md GV-ERR-MAINT
    liveRegion.announce('status', 'Maintenance mode active');
  }, [liveRegion]);

  return (
    <main
      role="main"
      aria-labelledby="maintenance-heading"
      data-gvw-id="GVW-165"
      data-route="/maintenance"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: tokens.color.surface[0],
        color: tokens.color.ink.primary,
        fontFamily: tokens.font.family.ui,
        padding: tokens.space[5],
        boxSizing: 'border-box',
      }}
    >
      <section
        style={{
          maxWidth: tokens.layout.standaloneStateExtraWide,
          width: '100%',
          background: tokens.color.surface[1],
          border: tokens.border.thin,
          borderLeft: `${tokens.space[1]}px solid ${tokens.color.semantic.warning}`,
          borderRadius: tokens.radius.lg,
          padding: `${tokens.space[6]}px ${tokens.space[5]}px`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: tokens.space[4],
          textAlign: 'center',
        }}
      >
        {/* Hero glyph per brief.md icon plan */}
        <div
          aria-hidden="true"
          style={{
            fontSize: 32,
            color: tokens.color.semantic.warning,
            fontFamily: 'Material Symbols Outlined',
          }}
        >
          construction
        </div>

        {/* Copy key: gvw.165.title from copy-deck.md */}
        <h1
          id="maintenance-heading"
          style={{
            margin: 0,
            fontSize: tokens.font.size['2xl'],
            lineHeight: tokens.font.lineHeight.tight,
            fontWeight: tokens.font.weight.semibold,
            color: tokens.color.ink.primary,
          }}
        >
          Maintenance
        </h1>

        {/* Copy key: gvw.165.body with endTime substitution */}
        <p
          style={{
            margin: 0,
            fontSize: tokens.font.size.md,
            lineHeight: tokens.font.lineHeight.normal,
            color: tokens.color.ink.secondary,
            maxWidth: '50ch',
          }}
        >
          GeoViewer is undergoing planned maintenance. We expect to be back at {endTime}.
        </p>

        {/* Recovery action per navigation-map.md §8 */}
        <button
          type="button"
          onClick={() => {
            window.open('/status', '_blank');
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: tokens.space[2],
            padding: `${tokens.space[3]}px ${tokens.space[4]}px`,
            background: tokens.color.accent.primary,
            color: tokens.color.accent.on,
            border: 'none',
            borderRadius: tokens.radius.md,
            fontSize: tokens.font.size.md,
            fontWeight: tokens.font.weight.medium,
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              fontFamily: 'Material Symbols Outlined',
              fontSize: 18,
            }}
          >
            schedule
          </span>
          Open status page
        </button>

        {/* Schedule icon per brief.md icon plan */}
        {etaParam && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: tokens.space[2],
              fontSize: tokens.font.size.sm,
              color: tokens.color.ink.muted,
              marginTop: tokens.space[2],
            }}
          >
            <span
              aria-hidden="true"
              style={{
                fontFamily: 'Material Symbols Outlined',
                fontSize: 16,
              }}
            >
              schedule
            </span>
            Expected completion: {endTime}
          </div>
        )}
      </section>
    </main>
  );
}